import { type FC } from 'react';
import { Tooltip, TooltipContent, TooltipPortal, TooltipTrigger } from '@/components/ui/tooltip';
import { useFormatTimestamp } from '@/hooks/useFormatTimestamp';
import { type Comment as CommentData } from '@/types/comments';
import { Badge } from '@/components/ui/badge';
import { useTranslations } from 'next-intl';

export const CommentAuthorAndTimestamp: FC<{
  comment: Pick<CommentData, 'createdBy' | 'createdAt' | 'updatedAt'>;
  isDraftReply?: boolean;
}> = ({ comment, isDraftReply = false }) => {
  const { formatTimestampDateAndTime, formatTimestampFromNow } = useFormatTimestamp();
  const tComments = useTranslations('inspector.comments');
  const authorName = comment.createdBy ? comment.createdBy.name : tComments('deletedUser');
  const isCollaborator = comment.createdBy?.role === 'guest';
  const isEdited = !isDraftReply && comment.updatedAt !== comment.createdAt;

  return (
    <div className="flex grow flex-col items-start">
      <Tooltip delayDuration={750}>
        <TooltipTrigger asChild>
          <p className="-mt-1 line-clamp-1 min-w-0 text-sm font-semibold wrap-anywhere text-text-base">{authorName}</p>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent>{authorName}</TooltipContent>
        </TooltipPortal>
      </Tooltip>
      <div className="flex items-center gap-x-0.5">
        {isDraftReply ? (
          <p className="line-clamp-1 text-[10px] text-text-gray">{tComments('now')}</p>
        ) : (
          <Tooltip delayDuration={750}>
            <TooltipTrigger asChild>
              <p className="line-clamp-1 text-[10px] text-text-gray">{formatTimestampFromNow(comment.createdAt)}</p>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent>{formatTimestampDateAndTime(comment.createdAt)}</TooltipContent>
            </TooltipPortal>
          </Tooltip>
        )}
        {isEdited && (
          <Tooltip delayDuration={750}>
            <TooltipTrigger asChild>
              <p data-cy="comment-edited-label" className="line-clamp-1 text-[10px] text-text-gray">
                {tComments('editedLabel')}
              </p>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent>{formatTimestampDateAndTime(comment.updatedAt)}</TooltipContent>
            </TooltipPortal>
          </Tooltip>
        )}
        {isCollaborator && (
          <Badge variant="outline" size="sm">
            {tComments('collaboratorBadge')}
          </Badge>
        )}
      </div>
    </div>
  );
};
